import { useCallback, useEffect, useRef, useState } from "react";
import { CoursesApi } from "api/controllers/CoursesApi";
import { IContentModel } from "models/dto/ZoomiLxp/Models/ContentModels/IContentModel";
import { IContentAsset } from "models/dto/ZoomiLxp/Models/ContentModels/IContentAsset";
import { useAppStore } from "store";

export const FILE_URL_MAX_AGE_MS = 4 * 60 * 1000;

export interface IContentFiles {
	url?: string;
	assets: IContentAsset[];
}

export const fetchContentFiles = async (courseId?: number, courseItemId?: number): Promise<IContentFiles | undefined> => {
	if (!courseId || !courseItemId) return undefined;
	try {
		const response = await CoursesApi.getCourseItemFiles(courseId, courseItemId);
		const data = response.data.data;
		return { url: data.fileContent?.url, assets: data.contentAssets ?? [] };
	} catch {
		return undefined;
	}
};

export const useContentFiles = (content: IContentModel, enabled = true) => {
	const { coursesStore } = useAppStore();
	const courseId = coursesStore.curActiveContent?.courseId;
	const courseItemId = coursesStore.curActiveContent?.courseItemId;
	const [files, setFiles] = useState<IContentFiles>();
	const fetchedAtRef = useRef(0);
	const contentRef = useRef(content);
	contentRef.current = content;

	const refresh = useCallback(async () => {
		const fresh = await fetchContentFiles(courseId, courseItemId);
		fetchedAtRef.current = Date.now();
		const fallback = contentRef.current;
		setFiles(
			fresh?.url
				? fresh
				: { url: fallback.fileContent?.url, assets: fresh?.assets ?? fallback.contentAssets ?? [] }
		);
	}, [courseId, courseItemId]);

	useEffect(() => {
		setFiles(undefined);
		if (enabled) void refresh();
	}, [refresh, content.id, enabled]);

	const isStale = useCallback(() => Date.now() - fetchedAtRef.current > FILE_URL_MAX_AGE_MS, []);

	return { files, refresh, isStale };
};

export const useContentDownload = (content: IContentModel) => {
	const { coursesStore } = useAppStore();
	const courseId = coursesStore.curActiveContent?.courseId;
	const courseItemId = coursesStore.curActiveContent?.courseItemId;
	const fallbackUrl = content.fileContent?.url;

	return useCallback(async () => {
		const fresh = await fetchContentFiles(courseId, courseItemId);
		const url = fresh?.url ?? fallbackUrl;
		if (!url) return;

		const link = document.createElement("a");
		link.href = url;
		link.target = "_blank";
		link.rel = "noopener noreferrer";
		link.click();
	}, [courseId, courseItemId, fallbackUrl]);
};
